import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CloseIcon } from '~/assets/icons';
import alertApi from '~/apis/alert';
import { adminRoute } from '~/utils/constants/route';
import { cast, handleApiError } from '~/utils/helper';
import type { IAlertDetail } from '~/utils/interface/alert';
import ProcessedStatus from './ProcessedStatus';
import UnprocessedStatus from './UnprocessedStatus';
import AlertDetailMap from './AlertDetailMap';

export type DetailAlertDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  onResolveSuccess?: () => void;
  data?: {
    id: string | number | null;
    imei?: string | null;
    full_name?: string | null;
    person_id?: number | null;
  };
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const looksLikeListAlertDetail = (o: Record<string, unknown>): boolean =>
  ('level' in o || 'imei' in o || 'person_id' in o || 'type' in o) && 'content' in o;

/**
 * Supports flat detail body, `{ result }`, or `{ result: { data } }` shapes.
 * Legacy `{ alert, sensor_info }` payloads are not mapped here (returns null).
 */
const parseAlertDetailPayload = (payload: unknown): IAlertDetail | null => {
  if (!isPlainObject(payload)) return null;

  if (looksLikeListAlertDetail(payload)) {
    return payload as unknown as IAlertDetail;
  }

  const result = payload.result;
  if (isPlainObject(result)) {
    if (looksLikeListAlertDetail(result)) return result as unknown as IAlertDetail;
    const nested = result.data;
    if (isPlainObject(nested) && looksLikeListAlertDetail(nested)) return nested as unknown as IAlertDetail;
  }

  return null;
};

/** Fixed 160×20 label cell so value column aligns across rows. */
const DetailLabelWrap = ({ children }: { children: ReactNode }) => (
  <Box className='flex h-5 w-[140px] shrink-0 items-center overflow-hidden opacity-100'>
    <Typography component='span' className='truncate text-sm leading-[140%] text-[#7B8794] font-["Manrope",sans-serif]'>
      {children}
    </Typography>
  </Box>
);

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box className='flex w-full flex-row items-start gap-5'>
    <DetailLabelWrap>{label}</DetailLabelWrap>
    <Box className='min-w-0 flex-1 text-left text-[14px] font-normal leading-[140%] tracking-[0.02em] text-[#161A1F] font-["Manrope",sans-serif] break-words'>
      {value}
    </Box>
  </Box>
);

const DetailAlertDialog = (props: DetailAlertDialogProps) => {
  const { t } = useTranslation('admin/alert');
  const navigate = useNavigate();
  const [detail, setDetail] = useState<IAlertDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    props.setOpen?.(open, needRefreshData);
  };

  const fetchDetail = useCallback(async (id: string) => {
    if (!id) return;
    setIsLoading(true);
    setDetail(null);
    try {
      const response = await alertApi.getAlertDetail(id);
      const parsed = parseAlertDetailPayload(response.data);
      setDetail(parsed);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!props.open) {
      setDetail(null);
      setIsLoading(false);
      return;
    }
    const id = props.data?.id;
    if (id === undefined || id === null || id === '') return;
    void fetchDetail(String(id));
  }, [props.open, props.data?.id, fetchDetail]);

  const formatLevel = (level?: number | null) => {
    if (level === undefined || level === null) return <Typography component='span'>—</Typography>;
    const levelTextClass = 'text-[14px] font-normal leading-[140%] tracking-[0.02em]';
    if (level === 3) {
      return (
        <Typography component='span' className={`${levelTextClass} text-[#C62828]`}>
          {t('alert_level_options.critical')}
        </Typography>
      );
    }
    if (level === 2 || level === 1) {
      return (
        <Typography component='span' className={`${levelTextClass} text-[#E48A1D]`}>
          {t('alert_level_options.warning')}
        </Typography>
      );
    }
    if (level === 0) {
      return (
        <Typography component='span' className={`${levelTextClass} text-[#7B8794]`}>
          {t('alert_level_options.notification')}
        </Typography>
      );
    }
    return (
      <Typography component='span' className={levelTextClass}>
        {String(level)}
      </Typography>
    );
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    return dayjs(value).format('HH:mm:ss DD/MM/YYYY');
  };

  const display = (value?: string | null) => {
    const v = cast(value);
    return v === '' || v === undefined ? '—' : v;
  };
  const personId = detail?.person_id ?? props.data?.person_id ?? null;

  const paperClass =
    'w-full max-w-[540px] rounded-[12px] bg-white shadow-lg m-4 sm:m-6 overflow-hidden h-auto max-h-[calc(100vh-2rem)] flex flex-col';

  return (
    <Dialog
      open={props.open}
      onClose={() => handleSetOpen(false)}
      fullWidth
      maxWidth={false}
      PaperProps={{ className: paperClass }}
    >
      <DialogTitle className='relative border-0 bg-transparent px-4 pb-4 pt-4 pr-14' sx={{ backgroundImage: 'none' }}>
        <Typography
          component='h1'
          className='bg-transparent pr-2 font-["Manrope",sans-serif] text-[20px] font-semibold leading-[140%] text-[#161A1F]'
        >
          {t('alertDetail.title')}
        </Typography>
        <IconButton
          type='button'
          onClick={() => handleSetOpen(false)}
          className='absolute right-2 top-2 rounded-lg text-[#161A1F]'
          aria-label={t('close', { ns: 'admin/common' })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        className='flex flex-1 flex-col overflow-y-auto px-4 pb-8 pt-2 gap-[28px]'
        sx={{ p: '0px 16px 32px 16px !important' }}
      >
        {isLoading && (
          <Box className='flex min-h-[200px] flex-1 items-center justify-center py-8'>
            <CircularProgress className='text-[#123B6D]' size={40} />
          </Box>
        )}

        {!isLoading && !detail && (
          <Typography className='py-6 text-center text-sm text-[#7B8794] font-["Manrope",sans-serif]'>
            {t('noData')}
          </Typography>
        )}

        {!isLoading && detail && (
          <>
            {/* Block 1 */}
            <Box className='flex flex-col gap-3'>
              <Typography
                component='h2'
                className='bg-transparent font-["Manrope",sans-serif] text-[16px] font-medium leading-[140%] tracking-[0.02em] text-[#161A1F]'
              >
                {t('alertDetail.block1Title')}
              </Typography>
              <Box
                component='section'
                className='flex flex-col gap-3 rounded-[12px] border border-[#D7DEE7] bg-[#F3F6F9] p-4'
              >
                <DetailRow label={t('alertDetail.type')} value={t('alertDetail.deviceTypeFixed')} />
                <DetailRow
                  label={t('alertDetail.deviceImei')}
                  value={display(detail.imei ?? props.data?.imei ?? null)}
                />
                <DetailRow label={t('alertDetail.levelField')} value={formatLevel(detail.level)} />
                <DetailRow
                  label={t('alertDetail.profile')}
                  value={
                    personId ? (
                      <Box
                        component='button'
                        type='button'
                        onClick={() => navigate(`${adminRoute.base}${adminRoute.person}/${personId}`)}
                        className='m-0 min-w-0 border-0 bg-transparent p-0 text-left text-[14px] font-normal leading-[140%] tracking-[0.02em] text-[#2E89C8] hover:underline'
                      >
                        {display(detail.full_name ?? props.data?.full_name ?? null)}
                      </Box>
                    ) : (
                      <Typography
                        component='span'
                        className='text-[14px] font-normal leading-[140%] tracking-[0.02em] text-[#2E89C8]'
                      >
                        {display(detail.full_name ?? props.data?.full_name ?? null)}
                      </Typography>
                    )
                  }
                />
                <DetailRow label={t('content')} value={display(detail.content)} />
                <DetailRow label={t('alertDetail.time')} value={formatDateTime(detail.created_at)} />
                <AlertDetailMap alertId={props.data?.id} />
              </Box>
            </Box>

            {/* Block 2 */}
            <Box className='flex flex-col gap-3'>
              <Typography
                component='h2'
                className='bg-transparent font-["Manrope",sans-serif] text-[16px] font-medium leading-[140%] tracking-[0.02em] text-[#161A1F]'
              >
                {t('alertDetail.block2Title')}
              </Typography>
              <Box
                component='section'
                className='flex flex-col gap-3 rounded-[12px] border border-[#D7DEE7] bg-[#F3F6F9] p-4'
              >
                {detail.status === 2 && <ProcessedStatus data={detail} />}
                {detail.status === 1 && (
                  <UnprocessedStatus
                    data={detail}
                    onRefresh={async (ctx) => {
                      const id = props.data?.id;
                      if (id === undefined || id === null || id === '') return;
                      try {
                        await alertApi.resolveAlert(String(id), { note: ctx.note });
                        handleSetOpen(false, true);
                        props.onResolveSuccess?.();
                      } catch (error) {
                        handleApiError(error);
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(DetailAlertDialog);
